import React, { useState } from 'react';
import { Upload, Settings, FileText, Zap } from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { TemplateLoader } from './services/templateLoader';
import CodeViewer from './components/CodeViewer';

const OpenAPIToMCPConverter = () => {
  const [step, setStep] = useState(1);
  const [spec, setSpec] = useState('');
  const [parsedSpec, setParsedSpec] = useState(null);
  const [excludeWrites, setExcludeWrites] = useState(true);
  const [excludedOperations, setExcludedOperations] = useState(new Set());
  const [projectName, setProjectName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedProject, setGeneratedProject] = useState<any>(null);
  const [uploadedFileName, setUploadedFileName] = useState('');


  const parseOpenAPISpec = (input: string): any => {
    try {
      let parsed: any;

      // Try JSON first
      try {
        parsed = JSON.parse(input);
      } catch {
        // If JSON fails, try YAML (basic parsing)
        try {
          // Simple YAML parsing for demonstration
          const lines = input.split('\n');
          const yamlObj: any = {};
          let currentObj: any = yamlObj;
          
          for (const line of lines) {
            const trimmed = line.trim();
            if (trimmed && !trimmed.startsWith('#')) {
              const colonIndex = trimmed.indexOf(':');
              if (colonIndex > 0) {
                const key = trimmed.substring(0, colonIndex).trim();
                const value = trimmed.substring(colonIndex + 1).trim();
                
                if (key === 'openapi' || key === 'swagger') {
                  yamlObj[key] = value.replace(/['"]/g, '');
                } else if (key === 'info') {
                  yamlObj.info = {};
                  currentObj = yamlObj.info;
                } else if (key === 'paths') {
                  yamlObj.paths = {};
                  currentObj = yamlObj.paths;
                } else if (key === 'title' && currentObj === yamlObj.info) {
                  currentObj.title = value.replace(/['"]/g, '');
                }
              }
            }
          }
          
          // Fallback structure if parsing fails
          if (!yamlObj.info) {
            yamlObj.info = { title: 'API' };
          }
          if (!yamlObj.paths) {
            yamlObj.paths = {};
          }
          
          parsed = yamlObj;
        } catch (e: any) {
          throw new Error('Invalid YAML format');
        }
      }

      // Validate basic OpenAPI structure
      if (!parsed.openapi && !parsed.swagger) {
        throw new Error('Not a valid OpenAPI specification');
      }

      if (!parsed.info || !parsed.info.title) {
        throw new Error('Missing required info.title field');
      }

      if (!parsed.paths) {
        parsed.paths = {};
      }

      return parsed;
    } catch (error) {
      throw new Error(`Failed to parse OpenAPI spec: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const extractOperations = (spec: any) => {
    const operations: any[] = [];
    const httpMethods = ['get', 'post', 'put', 'delete', 'patch', 'head', 'options'];
    
    for (const [path, pathObj] of Object.entries(spec.paths || {})) {
      for (const method of httpMethods) {
        if ((pathObj as any)[method]) {
          const operation = (pathObj as any)[method];
          operations.push({
            path,
            method: method.toUpperCase(),
            operationId: operation.operationId || `${method}_${path.replace(/[^a-zA-Z0-9]/g, '_')}`,
            summary: operation.summary || `${method.toUpperCase()} ${path}`,
            description: operation.description || '',
            isWrite: ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase())
          });
        }
      }
    }
    
    return operations;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setSpec(content);
      setUploadedFileName(file.name);
    };
    reader.readAsText(file);
  };

  const handleSpecUpload = () => {
    if (!spec.trim()) {
      alert('Please enter an OpenAPI specification or upload a file');
      return;
    }

    try {
      const parsed = parseOpenAPISpec(spec);
      setParsedSpec(parsed);
      setProjectName(parsed.info.title.toLowerCase().replace(/[^a-z0-9]/g, '-'));
      setStep(2);
    } catch (error: any) {
      alert(error.message);
    }
  };

  const clearSpec = () => {
    setSpec('');
    setUploadedFileName('');
  };

  const toggleOperationExclusion = (operationId: string) => {
    const newExcluded = new Set(excludedOperations);
    if (newExcluded.has(operationId)) {
      newExcluded.delete(operationId);
    } else {
      newExcluded.add(operationId);
    }
    setExcludedOperations(newExcluded);
  };

  const downloadProject = async () => {
    if (!generatedProject) return;

    const { files, name } = generatedProject;

    // Create a new JSZip instance
    const zip = new JSZip();

    // Add each file to the ZIP archive
    for (const [filePath, fileContent] of Object.entries(files)) {
      zip.file(filePath, fileContent as string);
    }

    // Generate the ZIP file
    const zipBlob = await zip.generateAsync({ 
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 6
      }
    });

    // Trigger download
    saveAs(zipBlob, `${name}.zip`);
  };

  const generateMCPProject = async () => {
    if (!parsedSpec) return;

    setIsGenerating(true);
    
    try {
      const operations = extractOperations(parsedSpec);
      const filteredOps = operations.filter(op => {
        if (excludeWrites && op.isWrite) return false;
        if (excludedOperations.has(op.operationId)) return false;
        return true;
      });

      // Generate project files using templates
      const projectFiles = await generateProjectFilesFromTemplates(parsedSpec, filteredOps, projectName);
      
      // Store generated project instead of downloading
      setGeneratedProject({
        files: projectFiles,
        name: projectName,
        operations: filteredOps
      });
      
      setIsGenerating(false);
      setStep(3);
    } catch (error) {
      console.error('Project generation failed:', error);
      // Fallback to hardcoded generation
      setTimeout(() => {
        const operations = extractOperations(parsedSpec);
        const filteredOps = operations.filter(op => {
          if (excludeWrites && op.isWrite) return false;
          if (excludedOperations.has(op.operationId)) return false;
          return true;
        });

        const projectFiles = generateProjectFiles(parsedSpec, filteredOps, projectName);
        
        setGeneratedProject({
          files: projectFiles,
          name: projectName,
          operations: filteredOps
        });
        
        setIsGenerating(false);
        setStep(3);
      }, 1000);
    }
  };

  const generateProjectFilesFromTemplates = async (spec: any, operations: any[], name: string) => {
    try {
      // Load templates
      const templates = await TemplateLoader.loadTemplates();
      
      // Generate variables for template substitution
      const variables = TemplateLoader.generateVariables(spec, operations, name);
      
      // Process templates with variables
      const processedFiles = TemplateLoader.processTemplates(templates, variables);
      
      return processedFiles;
    } catch (error) {
      console.error('Template-based generation failed:', error);
      throw error;
    }
  };

  const generateProjectFiles = (spec: any, operations: any[], name: string) => {
    const serverCode = generateServerCode(spec, operations, name);
    const clientCode = generateClientCode(spec, operations, name);
    const schemasCode = generateSchemasCode(spec);
    const pyprojectToml = generatePyprojectToml(spec, name);
    const readme = generateReadme(spec, operations, name);
    const requirements = generateRequirements();
    const envExample = generateEnvExample(spec);
    const testServer = generateTestServer(spec, operations, name);
    const testClient = generateTestClient(spec, operations, name);
    const conftest = generateConftest(spec, name);
    const mcpConfig = generateMCPConfig(spec, name);
    const claudeDesktopConfig = generateClaudeDesktopConfig(spec, name);
    const vscodeConfig = generateVSCodeConfig(spec, name);
    const makefileContent = generateMakefile(name);

    return {
      [`src/mcp_server_${name.replace(/-/g, '_')}/__init__.py`]: '',
      [`src/mcp_server_${name.replace(/-/g, '_')}/server.py`]: serverCode,
      [`src/mcp_server_${name.replace(/-/g, '_')}/client.py`]: clientCode,
      [`src/mcp_server_${name.replace(/-/g, '_')}/schemas.py`]: schemasCode,
      [`tests/__init__.py`]: '',
      [`tests/test_server.py`]: testServer,
      [`tests/test_client.py`]: testClient,
      [`tests/conftest.py`]: conftest,
      'pyproject.toml': pyprojectToml,
      'README.md': readme,
      'requirements.txt': requirements,
      '.env.example': envExample,
      'mcp.json': mcpConfig,
      'claude_desktop_config.json': claudeDesktopConfig,
      'vscode_settings.json': vscodeConfig,
      'Makefile': makefileContent
    };
  };

  const generateServerCode = (spec: any, operations: any[], name: string) => {
    return `"""
MCP Server for ${spec.info.title}
Generated from OpenAPI specification using FastMCP

This server provides MCP tools for interacting with the ${spec.info.title} API.
Each tool corresponds to an API endpoint and includes comprehensive error handling
and input validation.
"""

import asyncio
import logging
import os
from typing import Any, Dict, List, Optional, Union
from urllib.parse import urljoin

from fastmcp import FastMCP
from pydantic import BaseModel, Field, ValidationError

from .client import ${name.replace(/-/g, '_').replace(/^/, '').split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join('')}Client
from .schemas import *

# Set up logging
logging.basicConfig(level=logging.INFO)
logger = logging.getLogger(__name__)

# Initialize FastMCP app
mcp = FastMCP("${name}")

# Initialize client
client = ${name.replace(/-/g, '_').replace(/^/, '').split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join('')}Client()

${operations.map((op: any) => {
  const methodName = op.operationId;
  const description = op.summary || op.description || `${op.method.toUpperCase()} ${op.path}`;
  const isWrite = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(op.method.toUpperCase());
  
  return `@mcp.tool()
async def ${methodName}(${op.method === 'GET' ? 'params: Optional[Dict[str, Any]] = None' : 'data: Optional[Dict[str, Any]] = None'}) -> str:
    """
    ${description}
    
    ${isWrite ? '🔴 Write Operation - Modifies data' : '🟢 Read Operation - Safe to use'}
    
    Endpoint: ${op.method.toUpperCase()} ${op.path}
    ${op.description ? `Description: ${op.description}` : ''}
    
    Args:
        ${op.method === 'GET' ? 'params: Query parameters for the request' : 'data: Request body data'}
    
    Returns:
        JSON response from the API as a string
        
    Raises:
        Exception: If the API request fails
    """
    try:
        logger.info(f"Calling ${methodName} with ${op.method === 'GET' ? 'params' : 'data'}: {${op.method === 'GET' ? 'params' : 'data'}}")
        result = await client.${methodName.toLowerCase()}(
            ${op.method === 'GET' ? '**(params or {})' : '**(data or {})'}
        )
        return str(result)
    except Exception as e:
        logger.error(f"Error in ${methodName}: {e}")
        raise Exception(f"API call failed: {str(e)}")`;
}).join('\n\n')}

async def main():
    """Run the MCP server."""
    # Ensure client is initialized with environment variables
    await client.initialize_from_env()
    
    # Run the FastMCP server
    await mcp.run()

if __name__ == "__main__":
    asyncio.run(main())
`;
  };

  const generateClientCode = (spec: any, operations: any[], name: string) => {
    return `"""
API Client for ${spec.info.title}
Generated from OpenAPI specification
"""

import asyncio
import logging
from typing import Any, Dict, List, Optional
import aiohttp
import os
from urllib.parse import urljoin

logger = logging.getLogger(__name__)

class ${name.replace(/-/g, '_').replace(/^/, '').split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join('')}Client:
    def __init__(self, base_url: Optional[str] = None, api_key: Optional[str] = None):
        self.base_url = base_url or os.getenv("API_BASE_URL", "${spec.servers?.[0]?.url || 'https://api.example.com'}")
        self.api_key = api_key or os.getenv("API_KEY")
        self.session: Optional[aiohttp.ClientSession] = None
    
    async def _get_session(self) -> aiohttp.ClientSession:
        if self.session is None:
            headers = {"Content-Type": "application/json"}
            if self.api_key:
                headers["Authorization"] = f"Bearer {self.api_key}"
            
            self.session = aiohttp.ClientSession(
                headers=headers,
                timeout=aiohttp.ClientTimeout(total=30)
            )
        return self.session
    
    async def close(self):
        if self.session:
            await self.session.close()
            self.session = None
    
    async def _make_request(self, method: str, endpoint: str, **kwargs) -> Dict[str, Any]:
        session = await self._get_session()
        url = urljoin(self.base_url, endpoint)
        
        try:
            async with session.request(method, url, **kwargs) as response:
                response.raise_for_status()
                return await response.json()
        except aiohttp.ClientError as e:
            logger.error(f"API request failed: {e}")
            raise
    
${operations.map((op: any) => `    async def ${op.operationId.toLowerCase()}(self, **kwargs) -> Dict<string, Any>:
        """${op.summary}"""
        ${op.method === 'GET' ? 
          `return await self._make_request("${op.method}", "${op.path}", params=kwargs)` :
          `return await self._make_request("${op.method}", "${op.path}", json=kwargs)`
        }`).join('\n\n')}
`;
  };

  const generateSchemasCode = (spec: any) => {
    return `"""
Pydantic schemas for ${spec.info.title}
Generated from OpenAPI specification
"""

from typing import Any, Dict, List, Optional
from pydantic import BaseModel, Field
from datetime import datetime

# Base response model
class ApiResponse(BaseModel):
    success: bool = True
    data: Optional[Dict[str, Any]] = None
    message: Optional[str] = None

# Add your specific schemas here based on OpenAPI components
# This is a basic template - extend as needed

class ErrorResponse(BaseModel):
    error: str
    details: Optional[Dict[str, Any]] = None
`;
  };

  const generatePyprojectToml = (spec: any, name: string) => {
    return `[build-system]
requires = ["setuptools>=61.0", "wheel"]
build-backend = "setuptools.build_meta"

[project]
name = "mcp-server-${name}"
version = "1.0.0"
description = "MCP Server for ${spec.info.title}"
authors = [
    {name = "Generated", email = "generated@example.com"}
]
license = {text = "MIT"}
readme = "README.md"
requires-python = ">=3.8"
classifiers = [
    "Development Status :: 4 - Beta",
    "Intended Audience :: Developers",
    "License :: OSI Approved :: MIT License",
    "Programming Language :: Python :: 3",
    "Programming Language :: Python :: 3.8",
    "Programming Language :: Python :: 3.9",
    "Programming Language :: Python :: 3.10",
    "Programming Language :: Python :: 3.11",
    "Programming Language :: Python :: 3.12",
]
dependencies = [
    "aiohttp>=3.8.0",
    "mcp>=1.0.0",
    "pydantic>=2.0.0",
    "python-dotenv>=1.0.0",
]

[project.optional-dependencies]
dev = [
    "pytest>=7.0.0",
    "pytest-asyncio>=0.21.0",
    "black>=23.0.0",
    "isort>=5.12.0",
    "mypy>=1.0.0",
]

[project.scripts]
mcp-server-${name} = "mcp_server_${name.replace(/-/g, '_')}.server:main"

[tool.setuptools.packages.find]
where = ["src"]

[tool.black]
line-length = 88
target-version = ['py38']

[tool.isort]
profile = "black"
multi_line_output = 3
`;
  };

  const generateReadme = (spec: any, operations: any[], name: string) => {
    const writeOps = operations.filter(op => ['POST', 'PUT', 'PATCH', 'DELETE'].includes(op.method.toUpperCase()));
    const readOps = operations.filter(op => ['GET', 'HEAD', 'OPTIONS'].includes(op.method.toUpperCase()));
    
    return `# 🚀 MCP Server for ${spec.info.title}

${spec.info.description || 'Generated MCP server from OpenAPI specification using FastMCP'}

This is a **Model Context Protocol (MCP)** server that provides AI assistants with tools to interact with the ${spec.info.title} API. It's built using **FastMCP** for optimal performance and includes comprehensive testing, documentation, and configuration examples.

## ✨ Features

- 🔧 **${operations.length} API Tools** - Full coverage of ${spec.info.title} API endpoints
- 🧪 **Comprehensive Testing** - Unit tests with mocking and coverage reporting
- 📝 **Type Safety** - Full type hints and Pydantic models
- 🔒 **Secure** - Environment-based configuration with no hardcoded secrets
- 📊 **Observable** - Structured logging and error handling
- 🚀 **FastMCP** - High-performance MCP framework
- 🔄 **CI Ready** - Makefile with linting, testing, and formatting commands

## 🛠️ Quick Start

### Prerequisites

- Python 3.8+
- pip or pipenv

### Installation

1️⃣ **Clone and setup**
\`\`\`bash
git clone <your-repo>
cd ${name}
pip install -r requirements.txt
\`\`\`

2️⃣ **Configure environment**
\`\`\`bash
cp .env.example .env
# Edit .env with your API credentials
\`\`\`

3️⃣ **Test the installation**
\`\`\`bash
make test
\`\`\`

4️⃣ **Run the server**
\`\`\`bash
make run
\`\`\`

## ⚙️ Configuration

### Environment Variables

Create a \`.env\` file with the following variables:

\`\`\`env
# API Settings
API_BASE_URL=${spec.servers?.[0]?.url || 'https://api.example.com'}
API_KEY=your_api_key_here

# Logging
LOG_LEVEL=INFO  # DEBUG, INFO, WARNING, ERROR
\`\`\`

### MCP Client Configuration

#### For Claude Desktop

Add this to your Claude Desktop config file:

**macOS:** \`~/Library/Application Support/Claude/claude_desktop_config.json\`  
**Windows:** \`%APPDATA%\\Claude\\claude_desktop_config.json\`

\`\`\`json
{
  "mcpServers": {
    "${name}": {
      "command": "python",
      "args": ["-m", "mcp_server_${name.replace(/-/g, '_')}"],
      "env": {
        "API_BASE_URL": "${spec.servers?.[0]?.url || 'https://api.example.com'}",
        "API_KEY": "your-api-key-here"
      }
    }
  }
}
\`\`\`

#### For VSCode with GitHub Copilot

Add this to your VSCode settings (\`settings.json\`):

\`\`\`json
{
  "mcp.servers": [
    {
      "name": "${name}",
      "command": ["python", "-m", "mcp_server_${name.replace(/-/g, '_')}"],
      "env": {
        "API_BASE_URL": "${spec.servers?.[0]?.url || 'https://api.example.com'}",
        "API_KEY": "your-api-key-here"
      }
    }
  ]
}
\`\`\`

## 🔧 Available Tools

### 📖 Read Operations (${readOps.length} tools)
${readOps.length > 0 ? readOps.map((op: any) => `
- 🟢 **\`${op.operationId}\`** - ${op.summary || op.description || 'No description'}
  - **Method:** \`${op.method.toUpperCase()}\`
  - **Endpoint:** \`${op.path}\`
  - **Safe:** ✅ Read-only operation`).join('\n') : '\n*No read operations available*'}

### ✏️ Write Operations (${writeOps.length} tools)
${writeOps.length > 0 ? writeOps.map((op: any) => `
- 🔴 **\`${op.operationId}\`** - ${op.summary || op.description || 'No description'}
  - **Method:** \`${op.method.toUpperCase()}\`
  - **Endpoint:** \`${op.path}\`
  - **Caution:** ⚠️ Modifies data`).join('\n') : '\n*No write operations available*'}

## 🧪 Testing

### Run Tests
\`\`\`bash
# Run all tests with coverage
make test

# Run tests in watch mode  
make test-watch

# Run specific test file
pytest tests/test_server.py -v
\`\`\`

### Test Coverage
The test suite includes:
- ✅ **Server Tests** - MCP tool functionality
- ✅ **Client Tests** - API client functionality  
- ✅ **Error Handling** - Network and API error scenarios
- ✅ **Mocking** - Isolated unit tests
- ✅ **Integration** - End-to-end workflows

## 🏗️ Development

### Setup Development Environment
\`\`\`bash
make install-dev
\`\`\`

### Code Quality
\`\`\`bash
# Format code
make format

# Check linting
make lint

# Run type checking
mypy src/
\`\`\`

### Development Server
\`\`\`bash
# Run with debug logging
make dev
\`\`\`

### Available Make Commands
\`\`\`bash
make help  # Show all available commands
\`\`\`

## 📁 Project Structure

\`\`\`
${name}/
├── src/mcp_server_${name.replace(/-/g, '_')}/
│   ├── __init__.py
│   ├── server.py      # FastMCP server with tools
│   ├── client.py      # API client implementation  
│   └── schemas.py     # Pydantic models
├── tests/
│   ├── __init__.py
│   ├── conftest.py    # Test configuration
│   ├── test_server.py # Server tests
│   └── test_client.py # Client tests
├── .env.example       # Environment template
├── mcp.json          # MCP configuration example
├── Makefile          # Development commands
├── pyproject.toml    # Project configuration
├── requirements.txt  # Dependencies
└── README.md         # This file
\`\`\`

## 🔍 Troubleshooting

### Common Issues

**Import Errors**
\`\`\`bash
pip install -e .  # Install in development mode
\`\`\`

**API Authentication Failed**
- Check your \`.env\` file has the correct \`API_KEY\`
- Verify the \`API_BASE_URL\` is correct
- Test your credentials with curl or Postman first

**MCP Server Not Found**
- Ensure the server is installed: \`pip install -e .\`
- Check the MCP client configuration paths
- Verify environment variables are set

**Tests Failing**
\`\`\`bash
# Install test dependencies
pip install -r requirements.txt

# Run with verbose output
pytest -v --tb=short
\`\`\`

### Debug Mode

Run with debug logging to see detailed information:

\`\`\`bash
LOG_LEVEL=DEBUG make run
\`\`\`

## 🤝 Contributing

1. Fork the repository
2. Create a feature branch: \`git checkout -b feature-name\`
3. Make your changes
4. Run tests: \`make test\`
5. Format code: \`make format\`
6. Submit a pull request

## 📄 License

MIT License - see LICENSE file for details.

## 🙋‍♂️ Support

- 📖 [OpenAPI Specification](${spec.info?.contact?.url || '#'})
- 🐛 [Report Issues](https://github.com/your-repo/issues)
- 💬 [Discussions](https://github.com/your-repo/discussions)

---
**Generated with ❤️ by [OpenAPI to MCP Converter](https://github.com/your-converter-repo)**
`;
  };

  const generateRequirements = () => {
    return `# Core MCP dependencies
fastmcp>=0.9.0
pydantic>=2.0.0

# HTTP client
aiohttp>=3.8.0
httpx>=0.24.0

# Environment and configuration
python-dotenv>=1.0.0

# Testing dependencies
pytest>=7.0.0
pytest-asyncio>=0.21.0
pytest-mock>=3.10.0
pytest-cov>=4.0.0
aioresponses>=0.7.4

# Development dependencies
black>=23.0.0
isort>=5.12.0
mypy>=1.0.0
`;
  };

  const generateEnvExample = (spec: any) => {
    return `# API Configuration
API_BASE_URL=${spec.servers?.[0]?.url || 'https://api.example.com'}
API_KEY=your_api_key_here

# Logging
LOG_LEVEL=INFO
`;
  };

  const generateTestServer = (spec: any, operations: any[], name: string) => {
    return `"""
Test suite for ${spec.info.title} MCP Server
"""

import pytest
import asyncio
from unittest.mock import AsyncMock, patch, MagicMock
from typing import Dict, Any

from mcp_server_${name.replace(/-/g, '_')}.server import mcp, client


class TestMCPServer:
    """Test the MCP server functionality"""
    
    @pytest.fixture(autouse=True)
    def setup_method(self):
        """Set up test fixtures"""
        self.mock_client = AsyncMock()
        
    ${operations.map((op: any) => `
    @pytest.mark.asyncio
    async def test_${op.operationId.toLowerCase()}_success(self):
        """Test successful ${op.operationId} operation"""
        # Mock successful API response
        expected_result = {"status": "success", "data": "test_data"}
        
        with patch.object(client, '${op.operationId.toLowerCase()}', return_value=expected_result):
            # Call the MCP tool function
            result = await ${op.operationId}(${op.method === 'GET' ? 'params={"test": "value"}' : 'data={"test": "value"}'})
            
            assert str(expected_result) in result
            client.${op.operationId.toLowerCase()}.assert_called_once()
    
    @pytest.mark.asyncio 
    async def test_${op.operationId.toLowerCase()}_error(self):
        """Test error handling for ${op.operationId} operation"""
        with patch.object(client, '${op.operationId.toLowerCase()}', side_effect=Exception("API Error")):
            with pytest.raises(Exception, match="API call failed"):
                await ${op.operationId}(${op.method === 'GET' ? 'params={"test": "value"}' : 'data={"test": "value"}'})
                
    @pytest.mark.asyncio
    async def test_${op.operationId.toLowerCase()}_empty_params(self):
        """Test ${op.operationId} with empty parameters"""
        expected_result = {"status": "success"}
        
        with patch.object(client, '${op.operationId.toLowerCase()}', return_value=expected_result):
            result = await ${op.operationId}()
            assert str(expected_result) in result`).join('')}
`;
  };

  const generateTestClient = (spec: any, operations: any[], name: string) => {
    return `"""
Test suite for ${spec.info.title} API Client
"""

import pytest
import aiohttp
from unittest.mock import AsyncMock, patch, MagicMock
from aioresponses import aioresponses

from mcp_server_${name.replace(/-/g, '_')}.client import ${name.replace(/-/g, '_').replace(/^/, '').split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join('')}Client


class TestAPIClient:
    """Test the API client functionality"""
    
    @pytest.fixture
    def client(self):
        """Create a test client instance"""
        return ${name.replace(/-/g, '_').replace(/^/, '').split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join('')}Client()
    
    @pytest.fixture
    def mock_response(self):
        """Mock API response data"""
        return {"id": 1, "name": "test", "status": "active"}
    
    ${operations.map((op: any) => `
    @pytest.mark.asyncio
    async def test_${op.operationId.toLowerCase()}_success(self, client, mock_response):
        """Test successful ${op.operationId} API call"""
        with aioresponses() as m:
            url = f"{client.base_url}${op.path}"
            m.${op.method.toLowerCase()}(url, payload=mock_response)
            
            result = await client.${op.operationId.toLowerCase()}(${op.method === 'GET' ? '**{"param": "value"}' : '**{"data": "value"}'})
            
            assert result == mock_response
    
    @pytest.mark.asyncio
    async def test_${op.operationId.toLowerCase()}_http_error(self, client):
        """Test ${op.operationId} HTTP error handling"""
        with aioresponses() as m:
            url = f"{client.base_url}${op.path}"
            m.${op.method.toLowerCase()}(url, status=404)
            
            with pytest.raises(Exception):
                await client.${op.operationId.toLowerCase()}(${op.method === 'GET' ? '**{"param": "value"}' : '**{"data": "value"}'})
    
    @pytest.mark.asyncio
    async def test_${op.operationId.toLowerCase()}_network_error(self, client):
        """Test ${op.operationId} network error handling"""
        with patch('aiohttp.ClientSession.${op.method.toLowerCase()}', side_effect=aiohttp.ClientError("Network error")):
            with pytest.raises(Exception):
                await client.${op.operationId.toLowerCase()}(${op.method === 'GET' ? '**{"param": "value"}' : '**{"data": "value"}'})
                `).join('')}
                
    @pytest.mark.asyncio
    async def test_client_initialization(self, client):
        """Test client initialization"""
        assert client.base_url is not None
        assert hasattr(client, 'session')
        
    @pytest.mark.asyncio
    async def test_client_headers(self, client):
        """Test client headers configuration"""
        await client.initialize_from_env()
        # Add assertions based on your authentication method
        assert isinstance(client.headers, dict)
`;
  };

  const generateConftest = (spec: any, name: string) => {
    return `"""
Pytest configuration and shared fixtures
"""

import pytest
import asyncio
import os
from unittest.mock import patch

# Set test environment variables
os.environ.update({
    'API_BASE_URL': 'https://test-api.example.com',
    'API_KEY': 'test-api-key',
    'LOG_LEVEL': 'DEBUG'
})

@pytest.fixture(scope="session")
def event_loop():
    """Create an instance of the default event loop for the test session."""
    loop = asyncio.get_event_loop_policy().new_event_loop()
    yield loop
    loop.close()

@pytest.fixture
def mock_env():
    """Mock environment variables for testing"""
    env_vars = {
        'API_BASE_URL': 'https://test-api.example.com',
        'API_KEY': 'test-api-key-123',
        'LOG_LEVEL': 'DEBUG'
    }
    
    with patch.dict(os.environ, env_vars):
        yield env_vars

@pytest.fixture
def sample_api_response():
    """Sample API response for testing"""
    return {
        "id": 1,
        "name": "Test Item",
        "status": "active",
        "created_at": "2024-01-01T00:00:00Z"
    }
`;
  };

  const generateMCPConfig = (spec: any, name: string) => {
    return `{
  "mcpServers": {
    "${name}": {
      "command": "python",
      "args": ["-m", "mcp_server_${name.replace(/-/g, '_')}"],
      "env": {
        "API_BASE_URL": "${spec.servers?.[0]?.url || 'https://api.example.com'}",
        "API_KEY": "your-api-key-here"
      }
    }
  }
}`;
  };

  const generateClaudeDesktopConfig = (spec: any, name: string) => {
    return `{
  "mcpServers": {
    "${name}": {
      "command": "python",
      "args": ["-m", "mcp_server_${name.replace(/-/g, '_')}"],
      "env": {
        "API_BASE_URL": "${spec.servers?.[0]?.url || 'https://api.example.com'}",
        "API_KEY": "your-api-key-here"
      }
    }
  }
}

// Instructions for Claude Desktop:
// 1. Copy this configuration
// 2. On macOS: Edit ~/Library/Application Support/Claude/claude_desktop_config.json
// 3. On Windows: Edit %APPDATA%\\Claude\\claude_desktop_config.json
// 4. Replace "your-api-key-here" with your actual API key
// 5. Update API_BASE_URL if different from: ${spec.servers?.[0]?.url || 'https://api.example.com'}
// 6. Restart Claude Desktop
// 7. The ${name} tools should now be available in your conversations`;
  };

  const generateVSCodeConfig = (spec: any, name: string) => {
    return `{
  "mcp.servers": [
    {
      "name": "${name}",
      "command": ["python", "-m", "mcp_server_${name.replace(/-/g, '_')}"],
      "env": {
        "API_BASE_URL": "${spec.servers?.[0]?.url || 'https://api.example.com'}",
        "API_KEY": "your-api-key-here"
      }
    }
  ]
}

// Instructions for VSCode with GitHub Copilot:
// 1. Open VSCode
// 2. Go to File > Preferences > Settings (or Ctrl+,)
// 3. Click the "Open Settings (JSON)" icon in the top right
// 4. Add the above configuration to your settings.json
// 5. Replace "your-api-key-here" with your actual API key
// 6. Update API_BASE_URL if different from: ${spec.servers?.[0]?.url || 'https://api.example.com'}
// 7. Reload VSCode window (Ctrl+Shift+P > "Developer: Reload Window")
// 8. The ${name} tools should now be available to GitHub Copilot`;
  };

  const generateMakefile = (name: string) => {
    return `.PHONY: install test lint format clean run dev

# Variables
PYTHON := python3
PIP := pip3
PYTEST := pytest
BLACK := black
ISORT := isort
MYPY := mypy

# Installation
install:
	$(PIP) install -r requirements.txt

install-dev:
	$(PIP) install -r requirements.txt
	$(PIP) install -e .

# Testing
test:
	$(PYTEST) tests/ -v --cov=src/mcp_server_${name.replace(/-/g, '_')} --cov-report=html --cov-report=term

test-watch:
	$(PYTEST) tests/ -v --cov=src/mcp_server_${name.replace(/-/g, '_')} -f

# Code quality
lint:
	$(MYPY) src/
	$(BLACK) --check src/ tests/
	$(ISORT) --check-only src/ tests/

format:
	$(BLACK) src/ tests/
	$(ISORT) src/ tests/

# Development
run:
	$(PYTHON) -m mcp_server_${name.replace(/-/g, '_')}

dev:
	$(PYTHON) -m mcp_server_${name.replace(/-/g, '_')} --debug

# Cleanup
clean:
	find . -type f -name "*.pyc" -delete
	find . -type d -name "__pycache__" -delete
	rm -rf .coverage htmlcov/ .pytest_cache/ .mypy_cache/

# Build
build:
	$(PYTHON) -m build

# Help
help:
	@echo "Available commands:"
	@echo "  install     Install dependencies"
	@echo "  install-dev Install development dependencies"
	@echo "  test        Run tests with coverage"
	@echo "  test-watch  Run tests in watch mode"
	@echo "  lint        Run linting checks"
	@echo "  format      Format code"
	@echo "  run         Run the MCP server"
	@echo "  dev         Run in development mode"
	@echo "  clean       Remove cache files"
	@echo "  build       Build the package"
	@echo "  help        Show this help message"
`;
  };

  

  const getFilteredOperations = () => {
    if (!parsedSpec) return [];
    const operations = extractOperations(parsedSpec);
    return operations.filter((op: any) => {
      if (excludeWrites && op.isWrite) return false;
      if (excludedOperations.has(op.operationId)) return false;
      return true;
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-4">Convert Your OpenAPI Specification</h1>
        <p className="text-hpe-gray-300 max-w-2xl mx-auto">
          Transform your OpenAPI specifications into complete, production-ready Model Context Protocol (MCP) servers with comprehensive tests, configuration files, and development tools
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex justify-center mb-8">
        <div className="flex items-center space-x-4">
          {[
            { num: 1, label: 'Upload Spec', icon: Upload },
            { num: 2, label: 'Configure', icon: Settings },
            { num: 3, label: 'Generate', icon: Zap }
          ].map(({ num, label, icon: Icon }) => (
            <div key={num} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                step >= num ? 'bg-hpe-primary' : 'bg-hpe-dark-card'
              } transition-colors`}>
                {step > num ? <Icon className="w-5 h-5" /> : <span>{num}</span>}
              </div>
              <span className={`ml-2 ${step >= num ? 'text-white' : 'text-hpe-gray-400'}`}>
                {label}
              </span>
              {num < 3 && <div className="w-8 h-0.5 bg-hpe-dark-border mx-4" />}
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: Upload OpenAPI Spec */}
      {step === 1 && (
        <div className="max-w-4xl mx-auto">
          <div className="bg-hpe-dark-surface rounded-lg p-6 border border-hpe-dark-border">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Upload OpenAPI Specification
              </h2>
              <p className="text-hpe-gray-300 mb-4">
                Upload a file or paste your OpenAPI spec in JSON or YAML format:
              </p>
              
              {/* File Upload Section */}
              <div className="mb-4 p-4 border-2 border-dashed border-hpe-dark-border rounded-lg">
                <div className="flex items-center justify-center">
                  <div className="text-center">
                    <FileText className="w-12 h-12 text-hpe-gray-400 mx-auto mb-2" />
                    <label className="cursor-pointer">
                      <span className="text-hpe-primary hover:text-hpe-primary-dark font-medium">
                        Click to upload a file
                      </span>
                      <input
                        type="file"
                        accept=".json,.yaml,.yml"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>
                    <p className="text-hpe-gray-400 text-sm mt-1">
                      Supports .json, .yaml, .yml files
                    </p>
                    {uploadedFileName && (
                      <div className="mt-2 flex items-center justify-center gap-2">
                        <span className="text-hpe-primary text-sm">📄 {uploadedFileName}</span>
                        <button
                          onClick={clearSpec}
                          className="text-hpe-gray-400 hover:text-white text-sm"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center my-4">
                <div className="flex-1 border-t border-hpe-dark-border"></div>
                <span className="px-3 text-hpe-gray-400 text-sm">OR</span>
                <div className="flex-1 border-t border-hpe-dark-border"></div>
              </div>

              {/* Textarea for pasting */}
              <textarea
                value={spec}
                onChange={(e) => setSpec(e.target.value)}
                placeholder="Paste your OpenAPI specification here..."
                className="w-full h-80 bg-hpe-dark-bg border border-hpe-dark-border rounded-lg p-4 text-white font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              
              <div className="mt-4 flex justify-between items-center">
                {spec.trim() && (
                  <button
                    onClick={clearSpec}
                    className="px-4 py-2 text-hpe-gray-400 hover:text-white border border-hpe-dark-border hover:border-hpe-gray-400 rounded-lg transition-colors"
                  >
                    Clear
                  </button>
                )}
                <div className="flex-1"></div>
                <button
                  onClick={handleSpecUpload}
                  disabled={!spec.trim()}
                  className="px-6 py-2 bg-hpe-primary hover:bg-hpe-primary-dark disabled:bg-hpe-gray-600 rounded-lg font-medium transition-colors"
                >
                  Parse & Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Configure */}
        {step === 2 && parsedSpec && (
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-hpe-dark-surface rounded-lg p-6 border border-hpe-dark-border">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Configure Generation
              </h2>
              
              {/* Project Name */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Project Name</label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  className="w-full bg-hpe-dark-bg border border-hpe-dark-border rounded-lg p-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Exclude Write Operations */}
              <div className="mb-6">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={excludeWrites}
                    onChange={(e) => setExcludeWrites(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span>Exclude write operations (POST, PUT, DELETE, PATCH)</span>
                </label>
              </div>

              {/* Select Operations */}
              <div className="mb-6 p-4 bg-hpe-dark-bg rounded-lg border border-hpe-dark-border">
                <h3 className="font-medium mb-3">Select operations to include:</h3>
                <p className="text-hpe-gray-400 text-sm mb-4">
                  Check the operations you want to include in your MCP server. Write operations are filtered based on the option above.
                </p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {extractOperations(parsedSpec).filter(op => !excludeWrites || !op.isWrite).map((op) => (
                    <label key={op.operationId} className="flex items-center gap-3 p-2 hover:bg-hpe-dark-surface rounded">
                      <input
                        type="checkbox"
                        checked={!excludedOperations.has(op.operationId)}
                        onChange={() => toggleOperationExclusion(op.operationId)}
                        className="w-4 h-4"
                      />
                      <span className={`text-xs px-2 py-1 rounded ${
                        op.isWrite ? 'bg-hpe-orange' : 'bg-hpe-primary'
                      }`}>
                        {op.method}
                      </span>
                      <span className="flex-1 font-mono text-sm">{op.operationId}</span>
                      <span className="text-hpe-gray-400 text-sm">{op.summary}</span>
                    </label>
                  ))}
                </div>
                {extractOperations(parsedSpec).filter(op => !excludeWrites || !op.isWrite).length === 0 && (
                  <p className="text-hpe-orange-light text-sm">No operations available with current filters</p>
                )}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-2 bg-slate-600 hover:bg-slate-700 rounded-lg font-medium transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={generateMCPProject}
                  disabled={getFilteredOperations().length === 0}
                  className="px-6 py-2 bg-hpe-primary hover:bg-hpe-primary-dark disabled:bg-hpe-gray-600 rounded-lg font-medium transition-colors"
                >
                  Generate MCP Server
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Generate */}
        {step === 3 && (
          <>
            {isGenerating ? (
              <div className="max-w-6xl mx-auto">
                <div className="bg-hpe-dark-surface rounded-lg p-6 border border-hpe-dark-border">
                  <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <h2 className="text-xl font-semibold mb-2">Generating MCP Server...</h2>
                    <p className="text-hpe-gray-300">Please wait while we create your project files</p>
                  </div>
                </div>
              </div>
            ) : generatedProject ? (
              <div className="space-y-6">
                {/* Success Header */}
                <div className="max-w-4xl mx-auto text-center">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Zap className="w-8 h-8" />
                  </div>
                  <h2 className="text-2xl font-semibold mb-2">MCP Server Generated!</h2>
                  <p className="text-hpe-gray-300 mb-4">
                    Your complete MCP server project has been generated with {generatedProject.operations.length} API operations.
                  </p>
                  
                  {/* Operations Summary */}
                  <div className="bg-hpe-dark-surface rounded-lg p-4 mb-6 max-w-2xl mx-auto">
                    <h3 className="font-medium mb-3">Generated Operations:</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                      {generatedProject.operations.map((op: any) => (
                        <div key={op.operationId} className="flex items-center gap-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${
                            op.isWrite ? 'bg-hpe-orange' : 'bg-hpe-primary'
                          }`}>
                            {op.method}
                          </span>
                          <span className="font-mono text-xs">{op.operationId}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-center gap-4 mb-8">
                    <button
                      onClick={() => {
                        setStep(1);
                        setSpec('');
                        setParsedSpec(null);
                        setProjectName('');
                        setExcludedOperations(new Set());
                        setGeneratedProject(null);
                      }}
                      className="px-6 py-2 bg-hpe-gray-600 hover:bg-hpe-gray-700 rounded-lg font-medium transition-colors"
                    >
                      Convert Another
                    </button>
                  </div>
                </div>

                {/* Code Viewer */}
                <CodeViewer 
                  files={generatedProject.files}
                  projectName={generatedProject.name}
                  onDownload={downloadProject}
                />
              </div>
            ) : null}
          </>
        )}
    </div>
  );
};

export default OpenAPIToMCPConverter;